import {useQuery} from '@tanstack/react-query';

import {useSelector} from 'react-redux';

import {ENDPOINTS} from 'constants/endpoints';
import {ApplicationStoreType} from 'desktop/store';
import {useGetCustomerEmail} from 'hooks/getCustomerData/getCustomerData';
import {useDebounceValue} from 'hooks/useDebounce';
import {apiConfig} from 'libs/api/api';
import {isUhcEnabled} from 'libs/rollout';

import {getShippingEmail, getShippingPhoneNumber} from '../../store/selectors/checkout/checkout';
import {MODULE_KEY_CHECKOUT} from '../../store/selectors/constants';
import {emailValidationSchema} from '../../utils/deliveryFormValidation/deliveryFormValidation';

const SEND_SUBSCRIPTION_REQUEST_KEY = 'SEND_SUBSCRIPTION_REQUEST_KEY';

type SubscriptionType = {
    isEmailSubscribed: boolean;
    isSmsSubscribed: boolean;
};

const fetchSubscriptionData = async (destination: string): Promise<SubscriptionType | null> => {
    try {
        if (destination && !isUhcEnabled()) {
            const response = await apiConfig.instanceGusaBackend({
                baseURL: apiConfig.API_URL,
                data: {destination},
                method: 'post',
                url: ENDPOINTS.SUBSCRIPTION_STATUS,
            });
            return response.data;
        }

        return null;
    } catch (error) {
        throw new Error(error);
    }
};

const getSubscribedData = ({
    emailData,
    emailLoading,
    smsData,
    smsIsLoading,
}: {
    smsData?: {isSmsSubscribed: boolean} | null;
    emailData?: {isSmsSubscribed: boolean} | null;
    smsIsLoading: boolean;
    emailLoading: boolean;
}) => {
    return {
        data: emailData && smsData,
        isLoading: !smsIsLoading && !emailLoading,
        isSubscribed: !(smsData?.isSmsSubscribed || emailData?.isSmsSubscribed),
    };
};

const useGetCustomerOrShippingEmail = () => {
    const shippingEmail = useSelector(getShippingEmail);
    const customerEmail = useGetCustomerEmail();
    return customerEmail || shippingEmail;
};

const checkEmailIsValid = (debouncedEmail: string) =>
    emailValidationSchema.safeParse(debouncedEmail || '').success;

// TODO - GUSA-57076
/**
 * usePhoneSubscriptionRequest
 */
export const usePhoneSubscriptionRequest = () => {
    const phoneNumber = useSelector(getShippingPhoneNumber);
    const phoneNumberDebounced = useDebounceValue(phoneNumber, 3000);
    const email = useGetCustomerOrShippingEmail();
    const emailDebounced = useDebounceValue(email, 3000);

    const isUSA = useSelector(
        (store: ApplicationStoreType) =>
            store[MODULE_KEY_CHECKOUT].deliveryStep.shippingAddress.country.value === 'US',
    );

    const {data: smsData, isLoading: smsIsLoading} = useQuery({
        queryFn: () => fetchSubscriptionData(phoneNumber!),
        queryKey: [SEND_SUBSCRIPTION_REQUEST_KEY, phoneNumberDebounced],
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const {data: emailData, isLoading: emailLoading} = useQuery({
        enabled: checkEmailIsValid(emailDebounced),
        queryFn: () => fetchSubscriptionData(email),
        queryKey: [SEND_SUBSCRIPTION_REQUEST_KEY, emailDebounced],
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const {data, isLoading, isSubscribed} = getSubscribedData({
        emailData,
        emailLoading,
        smsData,
        smsIsLoading,
    });

    const isPhoneSubscribed = data && isSubscribed && isLoading && isUSA;
    return {
        data: smsData,
        isLoading,
        isSubscriptionPhoneAvailable: isPhoneSubscribed,
        isSubscriptionPhoneEnabled: isPhoneSubscribed,
    };
};
